import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, LockKey, Briefcase, User, Phone, CheckCircle, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function BeraterRegister() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("form"); // form | code | done
  const [form, setForm] = useState({ name: "", email: "", firma: "", telefon: "" });
  const [code, setCode] = useState("");
  const [invite, setInvite] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register-berater", { ...form, code: invite.trim() });
      setStep("code");
      toast.success("Wir haben dir einen Code per E-Mail geschickt.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: form.email, code });
      login(data.token, data.user);
      setStep("done");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (k, label, Icon, type = "text", required = true) => (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">{label}</label>
      <div className="relative">
        <Icon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
        <Input data-testid={`reg-${k}`} type={type} required={required} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-13 h-12 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
      </div>
    </div>
  );

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F8F9FA]">
      <div className="flex items-center justify-between px-6 pb-2 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
        {step !== "done" ? (
          <button data-testid="back-btn" onClick={() => (step === "code" ? setStep("form") : navigate("/login"))} className="flex h-10 w-10 items-center justify-center rounded-full text-[#0A1128] hover:bg-black/5">
            <ArrowLeft size={22} />
          </button>
        ) : <div className="w-10" />}
        <Logo />
        <div className="w-10" />
      </div>

      <div className="px-7 pt-10">
        {step === "form" && (
          <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="space-y-4" data-testid="berater-register-form">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Berater werden</h1>
            <p className="text-base text-[#718096]">Lege dein Profil an und erhalte deinen persönlichen QR-Code, um Kunden zu Empfehlungen einzuladen.</p>
            <div className="space-y-4 pt-2">
              {field("name", "Name", User)}
              {field("email", "E-Mail", EnvelopeSimple, "email")}
              {field("firma", "Firma (optional)", Briefcase, "text", false)}
              {field("telefon", "Telefon (optional)", Phone, "tel", false)}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Einladungs- / Gutscheincode</label>
              <div className="relative">
                <Ticket size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                <Input data-testid="reg-invite" required value={invite} onChange={(e) => setInvite(e.target.value.toUpperCase())} placeholder="z. B. A1B2C3" className="h-12 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base uppercase tracking-[0.15em] focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
              </div>
              <p className="mt-1.5 text-xs text-[#A0AEC0]">Du hast keinen Code? Frag deinen Administrator nach einer Einladung.</p>
            </div>
            <button data-testid="register-submit-btn" type="submit" disabled={loading} className="h-14 w-full rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60">
              {loading ? "Bitte warten…" : "Registrieren"}
            </button>
          </motion.form>
        )}

        {step === "code" && (
          <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={verify} className="space-y-5">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Code eingeben</h1>
            <p className="text-base text-[#718096]">Gesendet an <span className="font-semibold text-[#0A1128]">{form.email}</span>.</p>
            <div className="rounded-xl border border-[#C5A059]/40 bg-[#FBF8F1] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Code gesendet</p>
              <p className="mt-1 text-sm text-[#718096]">Bitte prüfe dein Postfach (ggf. auch Spam).</p>
            </div>
            <div className="relative">
              <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
              <Input data-testid="code-input" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-stelliger Code" className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-lg tracking-[0.3em] focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
            </div>
            <button data-testid="verify-code-btn" type="submit" disabled={loading || code.length < 6} className="h-14 w-full rounded-full bg-[#C5A059] text-base font-semibold text-[#0A1128] shadow-[0_8px_30px_rgba(197,160,89,0.3)] transition-all active:scale-[0.98] disabled:opacity-60">
              {loading ? "Prüfe…" : "Profil aktivieren"}
            </button>
          </motion.form>
        )}

        {step === "done" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="pt-8 text-center" data-testid="register-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#276749]/10">
              <CheckCircle size={36} weight="fill" color="#276749" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tight text-[#0A1128]">Willkommen!</h1>
            <p className="mt-3 text-base text-[#718096]">Dein Berater-Profil ist aktiv. In deinem Dashboard findest du deinen QR-Code und kannst Kunden einladen.</p>
            <button onClick={() => navigate("/berater")} className="mt-8 h-14 w-full rounded-full bg-[#0A1128] text-base font-semibold text-white active:scale-[0.98]">
              Zum Dashboard
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
